import React, { useEffect, useState } from "react";
import { FaUsers, FaGraduationCap, FaAward } from "react-icons/fa";
import { Button, Container, Eyebrow, IconTile } from "./ui";
import { fetchHomeBanners } from "../../services/banners";

const SLIDE_INTERVAL_MS = 5000;

const stats = [
  { icon: FaUsers, number: "4M+", label: "Students Trained" },
  { icon: FaGraduationCap, number: "1200+", label: "Campus Partners" },
  { icon: FaAward, number: "24+", label: "Years Experience" },
];

const HomeBanner = () => {
  const [banners, setBanners] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    let cancelled = false;

    fetchHomeBanners()
      .then((rows) => {
        if (cancelled) return;
        setBanners(rows);
        setActiveSlide(0);
      })
      .catch((error) => {
        if (cancelled) return;
        // Hero keeps its shape without slides rather than blocking the page.
        console.error("Error fetching home banners:", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Restarts whenever the slide count changes, so the modulo always matches
  // the number of banners actually loaded.
  useEffect(() => {
    if (banners.length < 2) return undefined;

    const id = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % banners.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [banners.length]);

  const scrollToAbout = () => {
    document
      .querySelector('[data-section="about"]')
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden bg-white pt-12 lg:pt-20">
      {/* Ambient wash behind the hero */}
      <div className="pointer-events-none absolute -right-40 -top-40 h-[32rem] w-[32rem] rounded-full bg-brand-50" />

      <Container className="relative">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
          {/* Copy — below the carousel on mobile/tablet, left of it on desktop */}
          <div className="order-2 text-center lg:order-1 lg:text-left">
            <Eyebrow className="mb-6">
              India's Leading Career Development Platform
            </Eyebrow>

            <h1 className="text-4xl font-bold leading-[1.1] text-ink sm:text-5xl xl:text-6xl">
              Reach Your <span className="text-brand">Career</span>
              <br />
              Goals
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-darkgrey lg:mx-0">
              India's Largest career development company with over 4 million
              students trained across 1200+ campuses
            </p>

            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
              <Button
                size="lg"
                onClick={() =>
                  window.open("https://app.smartica.co.in/en/courses/", "_blank")
                }
              >
                View Courses
              </Button>
              <Button size="lg" variant="tint" onClick={scrollToAbout}>
                Learn More
              </Button>
            </div>

            {/* Trust row — key social proof, styled as a highlighted card */}
            <div className="mt-10 rounded-2xl border border-brand-200 bg-brand-50 p-5 sm:p-6">
              <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider text-brand lg:justify-start">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                Trusted by leading institutions
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2 lg:justify-start">
                {["VIT University", "SRM Institute", "SASTRA University"].map(
                  (name) => (
                    <span
                      key={name}
                      className="rounded-full bg-white px-4 py-2 text-sm font-bold text-ink shadow-sm ring-1 ring-brand-100"
                    >
                      {name}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Circular carousel with geometric accents */}
          <div className="order-1 relative mx-auto w-full h-full max-w-md lg:order-2 lg:max-w-lg">
            <div className="relative aspect-square">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border border-dashed border-brand-300" />
              {/* Tinted disc */}
              <div className="absolute inset-[6%] rounded-full bg-brand-50" />

              {/* Rotating banner images, served from the CMS */}
              <div className="absolute inset-[10%] overflow-hidden rounded-full">
                {banners.map((banner, index) => (
                  <img
                    key={banner.id}
                    src={banner.url}
                    alt=""
                    aria-hidden={activeSlide !== index}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
                      activeSlide === index ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}
              </div>

              {/* Floating shapes */}
              <span className="absolute right-[4%] top-[16%] h-5 w-5 rounded-full bg-brand" />
              <span className="absolute bottom-[12%] left-[2%] h-8 w-8 rounded-full bg-brand" />
              <span className="absolute bottom-[4%] right-[18%] h-10 w-10 rounded-full bg-brand-300" />
              <span className="absolute right-[-2%] top-[52%] h-6 w-6 rounded-full bg-baseYellow" />
            </div>

            {/* Slide indicators — only meaningful once there's more than one slide */}
            {banners.length > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                {banners.map((banner, index) => (
                  <button
                    key={banner.id}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      activeSlide === index
                        ? "w-8 bg-brand"
                        : "w-2 bg-brand-300 hover:bg-brand/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stats card */}
        <div className="mt-16 rounded-3xl bg-brand-50 p-6 sm:p-8 lg:mt-20">
          <div className="grid gap-6 sm:grid-cols-3 sm:gap-0">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`flex items-center justify-center gap-4 sm:justify-start sm:px-6 ${
                  index > 0 ? "sm:border-l sm:border-brand-200" : ""
                }`}
              >
                <IconTile icon={stat.icon} tone="white" size="lg" />
                <div>
                  <div className="text-2xl font-bold text-ink sm:text-3xl">
                    {stat.number}
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-darkgrey">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default HomeBanner;
